import { takeLatest, put, call, all } from 'redux-saga/effects';
import ReportsTypes from './Reports.types';
import { fetchReportsData } from './Reports.services';
import { reportsSuccess, reportsFailure } from './Reports.action';
import {
	getManagementLeaveReportSuccess,
	getManagementLeaveReportFailure,
	getDesignationLeaveReportSuccess,
	getDesignationLeaveReportFailure,
	getMonthlyAttendanceSummaryReportSuccess,
	getMonthlyAttendanceSummaryReportFailure,
	getMonthlyCheckoutReportSuccess,
	getMonthlyCheckoutReportFailure,
} from './Reports.actions';
import {
	getManagementLeaveReportService,
	getDesignationLeaveReportService,
	getMonthlyAttendanceSummaryReportService,
	getMonthlyCheckoutReportService,
} from './Reports.services';
import { encryptData } from '../_common/enode-decode';
import AlertManager from '../../utils/alertManager';

export function* fetchReportsDataSaga(payload: any) {
	try {
		console.log('Starting report fetch with payload:', payload.payload);
		const key = 'fetchReportsDataKey';
		const encryptedData = encryptData(payload.payload, key);
		// @ts-ignore
		const responseData = yield call(fetchReportsData as any, encryptedData);
		console.log('API Response:', responseData);

		// Check if the response has data
		if (!responseData || !responseData.data) {
			console.error('Invalid API response:', responseData);
			yield put(reportsFailure('No data received from server'));
			return;
		}

		// Check if the response indicates an error
		if (responseData.data.status === 'error') {
			const errorMessage =
				responseData.data.message_text ||
				responseData.data.message ||
				'Server returned an error';
			console.error('API returned error:', {
				status: responseData.data.status,
				message_text: errorMessage,
				fullResponse: responseData.data,
			});
			yield put(reportsFailure(errorMessage));
			return;
		}

		// Success case - handle different report types
		console.log('Processing successful response:', responseData.data);

		switch (payload.payload.getMethod) {
			case 'GetSectionWiseAttendanceReport':
				// For section-wise reports, ensure we have valid data
				if (!responseData.data.data) {
					console.error('Invalid section-wise report data:', responseData);
					yield put(reportsFailure('No data found for the selected date range'));
					return;
				}
				yield put(
					reportsSuccess({
						status: 'success',
						data: responseData.data.data,
					}),
				);
				break;

			case 'GetDesignationWiseAttendanceReport':
				// For designation-wise reports, preserve the structure
				if (!responseData.data.data) {
					console.error('Invalid designation-wise report data:', responseData);
					yield put(reportsFailure('No data found for the selected date range'));
					return;
				}
				yield put(
					reportsSuccess({
						status: 'success',
						data: responseData.data.data,
					}),
				);
				break;

			case 'GetLeaveReport':
				// For leave reports, ensure we have the data array
				if (!responseData.data.data) {
					console.error('Invalid leave report data:', responseData);
					yield put(reportsFailure('No leave data found for the selected date range'));
					return;
				}
				yield put(
					reportsSuccess({
						status: 'success',
						data: responseData.data.data,
					}),
				);
				break;

			default:
				// For other reports (like regular attendance), ensure we have data
				if (!responseData.data.data) {
					console.error('Invalid report data:', responseData);
					yield put(reportsFailure('No data found for the selected date range'));
					return;
				}
				yield put(
					reportsSuccess({
						status: 'success',
						data: responseData.data.data,
					}),
				);
		}
	} catch (error: any) {
		console.error('Error in fetchReportsDataSaga:', error);
		let errorMessage = 'An error occurred while fetching the report';

		// Handle different error types
		if (error?.response?.data?.message_text) {
			errorMessage = error.response.data.message_text;
		} else if (error?.message) {
			errorMessage = error.message;
		} else if (typeof error === 'string') {
			errorMessage = error;
		}

		yield put(reportsFailure(errorMessage));
	}
}

export function* getManagementLeaveReportSaga(action: any): Generator<any, void, any> {
	try {
		console.log('Management Leave Report saga started with payload:', action);
		const key = 'fetchReportsDataKey';
		const encryptedData = encryptData(action.payload, key);

		const responseData = yield call(getManagementLeaveReportService as any, encryptedData);
		if (responseData.data.statusCode === '401') {
			// Show token validation alert
			AlertManager.showTokenValidationAlert();
			return;
		} else {
			if (responseData.data.status === 'success') {
				console.log('Management Leave Report successful, dispatching success action');
				yield put(getManagementLeaveReportSuccess(responseData.data.data));
			} else {
				console.log('Management Leave Report failed, dispatching failure action');
				yield put(getManagementLeaveReportFailure(responseData.data));
			}
		}
	} catch (error: any) {
		console.error('Error in getManagementLeaveReportSaga:', error);
		yield put(
			getManagementLeaveReportFailure(error?.response?.data?.message || 'An error occurred'),
		);
	}
}

export function* getDesignationLeaveReportSaga(action: any): Generator<any, void, any> {
	try {
		console.log('Designation Leave Report saga started with payload:', action);
		const key = 'getDesignationLeaveReportKey';
		const encryptedData = encryptData(action.payload, key);
		const responseData = yield call(getDesignationLeaveReportService as any, encryptedData);
		if (responseData.data.statusCode === '401') {
			// Show token validation alert
			AlertManager.showTokenValidationAlert();
			return;
		} else {
			if (responseData.data.status === 'success') {
				console.log('Designation Leave Report successful, dispatching success action');
				yield put(getDesignationLeaveReportSuccess(responseData.data.data));
			} else {
				console.log('Designation Leave Report failed, dispatching failure action');
				yield put(getDesignationLeaveReportFailure(responseData.data));
			}
		}
	} catch (error: any) {
		console.error('Error in getDesignationLeaveReportSaga:', error);
		yield put(
			getDesignationLeaveReportFailure(error?.response?.data?.message || 'An error occurred'),
		);
	}
}

export function* getMonthlyAttendanceSummaryReportSaga(action: any): Generator<any, void, any> {
	try {
		console.log('Monthly Attendance Summary Report saga started with payload:', action);
		const key = 'getMonthlyAttendanceSummaryReportKey';
		const encryptedData = encryptData(action.payload, key);

		const responseData = yield call(
			getMonthlyAttendanceSummaryReportService as any,
			encryptedData,
		);

		console.log('Monthly Attendance Summary Report API response:', responseData);

		// Validate and structure the response data
		if (responseData && typeof responseData === 'object') {
			// Ensure all required fields are present and properly typed
			const attendanceData = {
				month: String(responseData.month || ''),
				totalEmployees: Number(responseData.totalEmployees || 0),
				workingDays: Number(responseData.workingDays || 0),
				totalManDays: Number(responseData.totalManDays || 0),
				presentCount: Number(responseData.presentCount || 0),
				absentCount: Number(responseData.absentCount || 0),
				leaveCount: Number(responseData.leaveCount || 0),
				presentPercentage: String(responseData.presentPercentage || '0'),
				absentPercentage: String(responseData.absentPercentage || '0'),
				leavePercentage: String(responseData.leavePercentage || '0'),
			};

			console.log(
				'Monthly Attendance Summary Report successful, dispatching success action with validated data',
			);
			yield put(getMonthlyAttendanceSummaryReportSuccess(attendanceData));
		} else {
			console.log('Monthly Attendance Summary Report failed - invalid data structure');
			yield put(
				getMonthlyAttendanceSummaryReportFailure(
					'Invalid data structure received from server',
				),
			);
		}
	} catch (error: any) {
		console.error('Error in getMonthlyAttendanceSummaryReportSaga:', error);
		const errorMessage =
			typeof error === 'string'
				? error
				: error?.response?.data?.message_text ||
					error?.message ||
					'An error occurred while fetching the report';
		yield put(getMonthlyAttendanceSummaryReportFailure(errorMessage));
	}
}

export function* getMonthlyCheckoutReportSaga(action: any): Generator<any, void, any> {
	try {
		console.log('Monthly Checkout Report saga started with payload:', action);
		const key = 'getMonthlyCheckoutReportKey';
		const encryptedData = encryptData(action.payload, key);

		const responseData = yield call(getMonthlyCheckoutReportService as any, encryptedData);

		console.log('Monthly Checkout Report API response:', responseData);

		if (responseData && Array.isArray(responseData)) {
			console.log('Monthly Checkout Report successful, dispatching success action');
			yield put(getMonthlyCheckoutReportSuccess(responseData));
		} else {
			console.log('Monthly Checkout Report failed - invalid data structure');
			console.log('Response data type:', typeof responseData);
			console.log('Response data:', responseData);
			yield put(
				getMonthlyCheckoutReportFailure('Invalid data structure received from server'),
			);
		}
	} catch (error: any) {
		console.error('Error in getMonthlyCheckoutReportSaga:', error);
		const errorMessage =
			typeof error === 'string'
				? error
				: error?.response?.data?.message_text ||
					error?.message ||
					'An error occurred while fetching the report';
		yield put(getMonthlyCheckoutReportFailure(errorMessage));
	}
}

export function* watchReports() {
	console.log('Reports saga watcher started');
	yield all([
		takeLatest(ReportsTypes.REPORT_START, function* (action) {
			console.log('Reports saga caught REPORT_START action:', action);
			yield call(fetchReportsDataSaga, action);
		}),
		takeLatest(ReportsTypes.GET_MANAGEMENT_LEAVE_REPORT, getManagementLeaveReportSaga),
		takeLatest(ReportsTypes.GET_DESIGNATION_LEAVE_REPORT, getDesignationLeaveReportSaga),
		takeLatest(
			ReportsTypes.GET_MONTHLY_ATTENDANCE_SUMMARY_REPORT,
			getMonthlyAttendanceSummaryReportSaga,
		),
		takeLatest(ReportsTypes.GET_MONTHLY_CHECKOUT_REPORT, getMonthlyCheckoutReportSaga),
	]);
	console.log('Reports saga watcher setup completed');
}
